import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type AnimateType = 'fade' | 'fade-up' | 'fade-right' | 'fade-left';

/**
 * Anima un elemento cuando entra en el viewport al hacer scroll.
 * Sustituye a AOS: los estilos viven en `styles/_animations.scss` y esta
 * directiva solo se encarga de "cuándo" dispararlos.
 *
 * Uso (equivalente a los antiguos data-aos):
 *   <div appAnimateOnScroll="fade-right" [duration]="600" [delay]="500">
 *
 * SSR-safe: en servidor no hace nada, así que el contenido se renderiza
 * visible (bueno para SEO y navegadores sin JS).
 */
@Directive({
  selector: '[appAnimateOnScroll]',
  standalone: true,
})
export class AnimateOnScrollDirective implements OnInit, OnDestroy {
  /** Tipo de animación */
  @Input('appAnimateOnScroll') animation: AnimateType | '' = 'fade';
  /** Duración */
  @Input() duration = 800;
  /** Retraso */
  @Input() delay = 0;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly platformId = inject(PLATFORM_ID);
  private observer?: IntersectionObserver;

  ngOnInit(): void {
    // En servidor no ocultamos nada: el contenido queda visible.
    if (!isPlatformBrowser(this.platformId)) return;

    const host = this.el.nativeElement;
    const type = this.animation || 'fade';

    // Estado inicial (oculto) + parámetros de la animación.
    this.renderer.setAttribute(host, 'data-anim', type);
    host.style.setProperty('--anim-duration', `${this.duration}ms`);
    host.style.setProperty('--anim-delay', `${this.delay}ms`);

    // Si el navegador no soporta IntersectionObserver -> se muestra sin animar.
    if (typeof IntersectionObserver === 'undefined') {
      this.renderer.addClass(host, 'is-visible');
      return;
    }

    // Dispara una sola vez al entrar en el viewport.
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.renderer.addClass(host, 'is-visible');
            this.observer?.disconnect();
          }
        }
      },
      // rootMargin inferior de -50px ≈ el `offset: 50` que usabamos en AOS.
      { threshold: 0, rootMargin: '0px 0px -50px 0px' }
    );

    this.observer.observe(host);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
